import { useEffect, useState } from "react";

import { statsCoverage, type CoverageRow, type CoverageTrend, type StatsCoverage } from "@/lib/stats";
import { usePhone } from "@/lib/usePhone";

import { FootnotePanel, Panel, RowLabel, Td, Th } from "./Panel";
import { SkeletonRows } from "./Skeleton";
import { Bars } from "./chart/Bars";
import { useChartHover } from "./chart/HoverCard";
import { SegmentLegend, StackedBar } from "./chart/StackedBar";

/**
 * The four segments, and they are the same four words as Cards' state chips — 習得 is this
 * surface's own (a card past learning), but 学習中 and 新規 are `STATE_LABEL`'s, because they are
 * the same two states. One concept, one word, on every screen.
 *
 * `未登録` for the fourth: it counts words in the frequency universe that are **not in the
 * collection at all**, which is a claim about the collection rather than about a card's state, and
 * it is the canvas's own word. The table header below uses the same one — the canvas spells that
 * column `なし`, and two words for one quantity inside one panel is the drift this file exists to
 * avoid.
 */
const SEGMENTS = [
  { label: "習得", color: "var(--cover-learned)" },
  { label: "学習中", color: "var(--cover-learning)" },
  { label: "新規", color: "var(--cover-new)" },
  { label: "未登録", color: "var(--cover-absent)" },
];

/**
 * The headings, hoisted for the reason `History.tsx`'s are: `CoverageSkeleton` draws these same
 * two tables while the request is in flight, so every title and note here was written twice and
 * was free to disagree with itself between the loading state and the loaded one.
 */
const PANELS = {
  tiers: {
    title: "頻度帯別",
    check: "by-frequency-tier",
    note: "アニメ・ドラマ頻度表 · 99,999語",
  },
  tags: { title: "タグ別", check: "by-tag", note: "JLPTレベルと JMdict の優先度" },
  trends: {
    title: "網羅の推移",
    check: "cover-over-time",
    note: "四半期ごとの網羅率・ノート作成日基準",
  },
} as const;

/**
 * Coverage — `8c`. Where the holes are.
 *
 * Four segments per row, and the fourth is the point: **words in the frequency universe that are
 * not in the collection at all**. Anki shows completion as a two-segment count, which structurally
 * cannot say that.
 *
 * This is the one frame that puts its headings OUTSIDE the card — hence `variant="flush"`, whose
 * card has no horizontal padding so a row's rules reach its border. Everywhere else in Stats the
 * heading is inside; see `Panel`.
 */
export function Coverage({ deck }: { deck: string }) {
  const [data, setData] = useState<StatsCoverage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    void statsCoverage(deck)
      .then((found) => live && setData(found))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [deck]);

  if (error) return <p className="p-[26px] text-secondary text-accent-text">{error}</p>;
  if (!data) return <CoverageSkeleton />;

  return (
    <div className="flex flex-col gap-[14px]">
      {/* **The two tables sit side by side at 16:9**, which is the frame's arrangement and not
          merely a way to use the width: they are the same measurement against two different
          universes, and the whole point is to read one against the other. At 1147 they are stacked
          with the trends between them, because a ten-row bar table in half of 1147 has no track
          left to be a bar. */}
      <div className="flex flex-col gap-[14px] 2xl:flex-row 2xl:items-start">
        <div className="min-w-0 2xl:flex-1">
          <Panel {...PANELS.tiers} right={<SegmentLegend items={SEGMENTS} />} variant="flush">
            <RowTable rows={data.tiers} />
          </Panel>
        </div>

        {/* Between the two tables below 2xl, under both of them above it — so it is rendered once
            and moved, rather than written twice and left to drift. */}
        <div className="min-w-0 2xl:order-last 2xl:hidden">
          <TrendsPanel data={data} />
        </div>

        <div className="min-w-0 2xl:flex-1">
          <Panel {...PANELS.tags} variant="flush">
            <RowTable rows={data.tags} />
          </Panel>
        </div>
      </div>

      <div className="hidden 2xl:block">
        <TrendsPanel data={data} />
      </div>

      {/* Never silently dropped. A table that quietly omitted these would still add up, which is
          the whole reason the count is on screen rather than in a comment. */}
      {data.unenriched > 0 && (
        <FootnotePanel>
          <strong className="font-semibold text-ink">
            {data.unenriched.toLocaleString()}ノートに頻度タグがありません
          </strong>{" "}
          — 未補完のため、上のすべての棒から除外。このデッキの{data.totalNotes.toLocaleString()}
          ノート中。
        </FootnotePanel>
      )}
    </div>
  );
}

/**
 * Cover over time, as four small multiples.
 *
 * Its own component because it is drawn in two places — between the tables below `2xl`, under both
 * of them above it — and the alternative is the same JSX written twice and drifting.
 */
function TrendsPanel({ data }: { data: StatsCoverage }) {
  return (
    <Panel {...PANELS.trends} variant="bare">
      <div className="grid gap-[12px] sm:grid-cols-2 lg:grid-cols-4">
        {data.trends.map((trend) => (
          <TrendCard key={trend.tag} trend={trend} />
        ))}
      </div>
    </Panel>
  );
}

function RowTable({ rows }: { rows: CoverageRow[] }) {
  const { onEnter, onLeave, card } = useChartHover();
  const isPhone = usePhone();

  // **The bar is the panel, and at 390px the table took all the width from it.** Four numeric
  // columns are 290px of fixed width before the label has any, so the bar's `<td>` — the only cell
  // with no width of its own — resolved to about a pixel: a vertical hairline beside every tier,
  // which reads as a divider rather than as a chart that lost its space. Measured, not guessed.
  //
  // So the phone gives the bar its own line, full width, with the label above it and the counts
  // below. Nothing is dropped: `learned · new · absent` still read left to right in the desktop's
  // order, and `cover` stays beside the label because it is the row's answer.
  if (isPhone) {
    return (
      <>
        {rows.map((row) => (
          <div key={row.tag} className="flex flex-col gap-[5px] border-t border-line-soft py-[9px] px-[16px]">
            <div className="flex items-baseline gap-[8px]">
              <span className="min-w-0 flex-1 truncate text-[14px]/[1.4] font-semibold text-ink">
                {row.label}
              </span>
              <span className="font-mono text-[14px]/[1.4] font-semibold text-ink">
                {row.cover === null ? "—" : `${(row.cover * 100).toFixed(1)}%`}
              </span>
            </div>
            <StackedBar
              height={14}
              hatched={row.universe === null}
              segments={segmentsFor(row)}
              onEnter={onEnter}
              onLeave={onLeave}
            />
            <span className="font-mono text-[14px]/[1.4] text-ink-3">
              習得{row.learned.toLocaleString()} · 新規{row.new.toLocaleString()} · 未登録
              {row.absent === null ? "—" : row.absent.toLocaleString()}
            </span>
          </div>
        ))}
        {card}
      </>
    );
  }

  return (
    <>
      <table className="w-full">
      <thead>
        <tr>
          <Th align="left" width={112} />
          <Th />
          <Th width={78}>習得</Th>
          <Th width={62}>新規</Th>
          <Th width={78}>未登録</Th>
          <Th width={72}>網羅</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          return (
            <tr key={row.tag} className="border-t border-line-soft">
              <RowLabel inset>{row.label}</RowLabel>
              <td className="py-[7px] pr-[10px]">
                <StackedBar
                  height={18}
                  hatched={row.universe === null}
                  segments={segmentsFor(row)}
                  onEnter={onEnter}
                  onLeave={onLeave}
                />
              </td>
              {/* The hierarchy the design has and a flat table loses: `absent` and `cover` are what
                  the panel is FOR, so they are ink; learned and new are supporting and are ink-3. */}
              <Td tone="muted">{row.learned.toLocaleString()}</Td>
              <Td tone="muted">{row.new.toLocaleString()}</Td>
              <Td>{row.absent === null ? "—" : row.absent.toLocaleString()}</Td>
              <td className="py-[7px] pl-[6px] pr-[16px] text-right font-mono text-[14px]/[19.6px] font-semibold text-ink">
                {row.cover === null ? "—" : `${(row.cover * 100).toFixed(1)}%`}
              </td>
            </tr>
          );
        })}
        </tbody>
      </table>
      {card}
    </>
  );
}

/**
 * A row's four segments, and the hover card each of them carries.
 *
 * Shared by the table and the phone's stack rather than written twice: a segment's *share* is the
 * one number in this panel that cannot be recovered from what is drawn beside it, so two copies of
 * this would be two chances for one form to quietly answer a different question from the other.
 *
 * The denominator is the row's universe where it has one — the JMdict priority markers have no term
 * list, so their rows fall back to what is held, which is why `absent` is `null` there and the
 * hatching says the row is not measured against anything.
 */
function segmentsFor(row: CoverageRow) {
  const universe = row.universe ?? row.learned + row.learning + row.new;
  const share = (value: number) =>
    universe > 0 ? `${((100 * value) / universe).toFixed(1)}%` : "—";
  return (
    [
      ["習得", row.learned, "var(--cover-learned)"],
      ["学習中", row.learning, "var(--cover-learning)"],
      ["新規", row.new, "var(--cover-new)"],
      ["未登録", row.absent ?? 0, "var(--cover-absent)"],
    ] as const
  ).map(([label, value, color]) => ({
    value,
    color,
    hover: {
      title: `${row.label} · ${label}`,
      rows: [
        { label: "語数", value: value.toLocaleString() },
        { label: "この帯に占める割合", value: share(value) },
      ],
    },
  }));
}

/**
 * One tier's trajectory: its name and current cover across the top, the quarterly bars beneath, and
 * the projected completion at the foot — its own bordered card, as the frames draw it (`12px 14px`).
 *
 * The eta goes red when it reads *never*, which is the one outcome worth spotting from across the
 * screen: a tier the current rate does not finish.
 */
function TrendCard({ trend }: { trend: CoverageTrend }) {
  const { onEnter, onLeave, card } = useChartHover();
  const peak = Math.max(...trend.points.map((p) => p.cover), 0.01);
  const never = trend.current === null || trend.current <= 0;
  return (
    <div className="flex flex-col gap-[8px] rounded-[var(--r-3)] border border-line-strong bg-panel px-[14px] py-[12px]">
      <div className="flex items-baseline justify-between gap-[8px]">
        <span className="text-[14px]/[19.6px] font-semibold text-ink">{trend.label}</span>
        <span className="font-mono text-[17px]/[1] font-semibold text-ink">
          {trend.current === null ? "—" : `${(trend.current * 100).toFixed(1)}%`}
        </span>
      </div>
      <Bars
        height={46}
        scaleMax={peak}
        bars={trend.points.map((point) => ({
          value: point.cover,
          color: "var(--cover-new)",
          hover: {
            title: `${trend.label} · ${point.label}`,
            rows: [{ label: "網羅", value: `${(point.cover * 100).toFixed(1)}%` }],
          },
        }))}
        onEnter={onEnter}
        onLeave={onLeave}
      />
      {/* **The projection, which the design puts under every one of these** (`8c`:
          `100% in 2031, at 3.9/day`). It is Pace's arithmetic at tier scale — a trailing add rate
          extended to what is left — and it is the whole reason the panel is four small charts
          rather than one number each: Top 1k finishes, Top 10k does not, and the difference is
          the same argument Pace makes from the other end.

          `never` is red and is the honest answer for most tiers. It covers two cases that read the
          same from here: a rate of zero, and a rate so slow the finish is past the horizon. */}
      <span
        className="text-[14px]/[1.4]"
        style={{ color: trend.completesOn === null ? "var(--accent-text)" : "var(--ink-3)" }}
      >
        {never
          ? "網羅なし"
          : trend.completesOn === null
            ? `${trend.addRate.toFixed(1)}枚/日では到達せず`
            : `${new Date(`${trend.completesOn}T00:00:00`).getFullYear()}年に100%・${trend.addRate.toFixed(1)}枚/日`}
      </span>
      {card}
    </div>
  );
}


/** Coverage before its payload — the two tables side by side, and the tier cards beneath. */
function CoverageSkeleton() {
  const table = (panel: (typeof PANELS)["tiers" | "tags"], rows: number) => (
    <Panel {...panel} variant="flush">
      <div className="px-[16px] py-[10px]">
        <SkeletonRows rows={rows} height={16} gap={18} />
      </div>
    </Panel>
  );
  return (
    <div className="flex flex-col gap-[14px]">
      <div className="flex flex-col gap-[14px] 2xl:flex-row 2xl:items-start">
        <div className="min-w-0 2xl:flex-1">{table(PANELS.tiers, 10)}</div>
        <div className="min-w-0 2xl:flex-1">{table(PANELS.tags, 11)}</div>
      </div>
    </div>
  );
}
